import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Form,
  SpaceBetween,
  Spinner,
  TextContent,
  WizardProps,
} from "@amzn/awsui-components-console";
import { RootState } from "../../redux/store";

import * as BusinessDetails from "../../forms/Cargo/Stage1/BusinessDetails";
import * as ShipmentDetails from "../../forms/Cargo/Stage1/ShipmentDetails";
import * as IncidentDetails from "../../forms/Cargo/Stage1/IncidentDetails";
import * as ClaimDetails from "../../forms/Cargo/Stage2/ClaimDetails";
import * as FileAttachments from "../../forms/Cargo/Stage3/FileAttachments";

import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { resetAppState, setLineOfBusiness } from "../appSlice";
import { useAppSelector } from "src/redux/hooks";
import { FormStateType } from "src/forms/types";
import { resetFormsState, resetUpdatedForm } from "src/forms/formsSlice";
import WizardWrapper from "../../components/wizard/WizardWrapper";
import React from "react";
import ResetFormModal from "../../components/reset-form-modal/ResetFormModal";
import { LineOfBusiness, ResetFormModalContents, SimLinkClaimModel } from "../types";
import { CARGO_STEP_NAMES, FORM_VALIDATIONS } from "./constants";
import {
  createCargoClaim,
  getCargoClaim,
  listCargoClaims,
  resetCargoAppState,
  setCargoStage,
  setClaimNumber,
  setValueInCargoAppState,
  updateCargoClaim,
  updateCargoClaimAttachments,
} from "./cargoAppSlice";
import ClaimSubmitted from "../../views/ClaimSubmitted";
import {
  CLAIM_ALREADY_COMPLETED_MODAL_TITLE,
  CLAIM_RETRIEVAL_ERROR_MODAL_TITLE,
  DUPLICATE_CHECK_ERROR_MODAL_TITLE,
  DUPLICATE_MODAL_TITLE,
  FORM_PAGE_ERROR_MESSAGE,
} from "../constants";
import {
  resetWizardState,
  setSkipToStep,
} from "../../components/wizard/wizardSlice";
import { REVIEW_PAGE_CONSTANTS } from "../../forms/constants";
import { CARGO_STAGES } from "./types";
import FormContent from "../../forms/FormContent";

import { useLocation, useSearchParams } from "react-router-dom";
import { isDevEnvironment } from "../../environmentInfo/EnvironmentInfo";
import ReviewPage from "../../forms/reviewPage/ReviewPage";
import {
  BUSINESS_DETAILS_CONSTANTS,
  CLAIM_DETAILS_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
  INCIDENT_DETAILS_CONSTANTS,
  SHIPMENT_DETAILS_CONSTANTS,
} from "../../forms/Cargo/constants";
import { cloneDeep } from "lodash";
import { resetReviewPageState } from "../../forms/reviewPage/reviewPageSlice";
import moment from "moment";
import { formatCurrencyStringToNumber } from "./utils";
import SimLinkGenerator from "../../components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "../../components/simLinkGenerator/types";
import { convertFormToSymLinkClaimModel } from "src/utils/convertFormToSymlink";

// Modal content components
const DuplicateCheckErrorContent = () => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          An error occurred while checking for duplicate claims. This may be due
          to a temporary network issue or server problem.
        </div>
        <br />
        <div>
          You can retry the duplicate check or continue with your claim
          submission.
        </div>
      </div>
    </Box>
  </TextContent>
);

const DuplicateClaimsFoundContent = ({
  incidentDate,
  totalClaimValue,
  laneOrigination,
}: {
  incidentDate: string;
  totalClaimValue: string;
  laneOrigination: string;
}) => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          One or more claims have already been submitted with the information
          you entered:
          <ul>
            <li>Incident Date: {incidentDate}</li>
            <li>Total Claim Value: {totalClaimValue}</li>
            <li>Lane Origination: {laneOrigination}</li>
          </ul>
        </div>
        <div>
          <b>Please verify this is not a duplicate submission.</b>
        </div>
        <br />
        <div>Would you like to continue filing this claim?</div>
      </div>
    </Box>
  </TextContent>
);

const ClaimRetrievalErrorContent = () => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          We encountered an issue retrieving your claim information. This may be
          due to:
        </div>
        <ul>
          <li>A temporary network connectivity issue</li>
          <li>The claim link may be invalid or expired</li>
          <li>A temporary system issue</li>
        </ul>
        <br />
        <div>
          <b>Please try the following steps:</b>
        </div>
        <ol>
          <li>Reload this page and try again</li>
          <li>
            Check that you&apos;re using the correct claim link from your email
          </li>
          <li>
            If the issue persists, please contact our support team by opening a{" "}
            <SimLinkGenerator
              target="_blank"
              simType={SIM_TYPES.REPORT_A_BUG}
              ariaLabel="Press this link to open a new tab where you will be able to create a ticket to report the claim retrieval issue."
            >
              support ticket
            </SimLinkGenerator>
          </li>
        </ol>
      </div>
    </Box>
  </TextContent>
);

const ClaimAlreadyCompletedContent = () => (
  <TextContent>
    <Box textAlign="left">
      <div className="modal-text">
        <div>
          This claim has already been fully submitted and completed. No further
          action is required.
        </div>
        <br />
        <div>
          <b>What you can do:</b>
        </div>
        <ul>
          <li>View your submitted claims in the Claims History section</li>
          <li>Start a new claim if you need to submit another claim</li>
          <li>
            Contact our support team if you have questions about this claim by
            opening a{" "}
            <SimLinkGenerator
              target="_blank"
              simType={SIM_TYPES.REPORT_A_BUG}
              ariaLabel="Press this link to open a new tab where you will be able to create a ticket about this completed claim."
            >
              support ticket
            </SimLinkGenerator>
          </li>
        </ul>
      </div>
    </Box>
  </TextContent>
);

// Deep clone form configs to prevent mutations across component instances
// This ensures each form instance has its own isolated configuration state
const DEFAULT_FORM_CONFIGS = {
  [BUSINESS_DETAILS_CONSTANTS.FORM_NAME]: cloneDeep(BusinessDetails.formConfig),
  [SHIPMENT_DETAILS_CONSTANTS.FORM_NAME]: cloneDeep(ShipmentDetails.formConfig),
  [INCIDENT_DETAILS_CONSTANTS.FORM_NAME]: cloneDeep(IncidentDetails.formConfig),
  [CLAIM_DETAILS_CONSTANTS.FORM_NAME]: cloneDeep(ClaimDetails.formConfig),
  [FILE_ATTACHMENTS_CONSTANTS.FORM_NAME]: cloneDeep(FileAttachments.formConfig),
};
function CargoClaimIntakeApp() {
  // Force wizard re-render by changing key when form is reset
  // This ensures all internal wizard state is completely cleared
  const wizardKey = useRef(0);

  const [submitClicked, setSubmitClicked] = useState(false);
  const [formattedFormErrors, setFormattedFormErrors] = useState(null);
  const [resetFormModalOpen, setResetFormModalOpen] = useState(false);
  const [resetFormModalContents, setResetFormModalContents] =
    useState<ResetFormModalContents>({
      title: "",
      description: null,
      confirm: "",
      cancel: "",
    });

  const [currentStep, setCurrentStep] = useState(0);
  const [renderSubmitScreen, setRenderSubmitScreen] = useState(false);
  const [stepsToRender, setStepsToRender] = useState<WizardProps.Step[]>([]);
  const [claimModel, setClaimModel] = useState<
    | SimLinkClaimModel
    | undefined
  >(undefined);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  //Redux Auth state
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { goingBack, currentIndex } = useAppSelector(
    (state: RootState) => state.wizard
  );

  const formStates = useAppSelector(
    (state: RootState) => state.forms.allFormStates
  );
  const { validating, errors } = useAppSelector(
    (state: RootState) => state.forms
  );

  // Get amazonClaimReferenceId from app slice
  const { amazonClaimReferenceId } = useAppSelector(
    (state: RootState) => state.app
  );

  // Get cargo-specific state from cargoApp slice
  const {
    loadingCargoClaim,
    cargoSubmittedStage,
    cargoIncidentDate,
    claimSubmitSuccess,
    currentStage,
    claimNumber,
    duplicateClaimChecked,
    cargoClaimsList,
    fetchingClaimsList,
    listCargoClaimsError,
  } = useAppSelector((state: RootState) => state.cargoApp);

  const [params] = useSearchParams();
  const [hasAutoNavigated, setHasAutoNavigated] = useState(false);
  const [duplicateModalDismissed, setDuplicateModalDismissed] = useState(false);

  // Initialize stage from URL parameter to prevent flicker
  const [initialStageSet, setInitialStageSet] = useState(false);

  // Error modal state for claim retrieval issues
  const [claimRetrievalErrorModalOpen, setClaimRetrievalErrorModalOpen] =
    useState(false);

  // Modal state for completed claims
  const [claimAlreadyCompletedModalOpen, setClaimAlreadyCompletedModalOpen] =
    useState(false);

  const incidentDate =
    formStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME][
      INCIDENT_DETAILS_CONSTANTS.INCIDENT_DATE
    ].value;
  const totalClaimValue =
    formStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME][
      INCIDENT_DETAILS_CONSTANTS.TOTAL_CLAIM_VALUE
    ].value;

  const laneOrigination =
    formStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME][
      SHIPMENT_DETAILS_CONSTANTS.LANE_ORIGINATION
    ].value;
  const CARGO_STAGE1_STEPS: WizardProps.Step[] = [
    {
      title: BusinessDetails.formHeader,
      description: BusinessDetails.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={BusinessDetails.formName}
            formConfig={BusinessDetails.formConfig}
            formValidation={BusinessDetails.formValidation}
            data-testid="business-details-form"
            key="business-details-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: ShipmentDetails.formHeader,
      description: ShipmentDetails.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={ShipmentDetails.formName}
            formConfig={ShipmentDetails.formConfig}
            formValidation={ShipmentDetails.formValidation}
            data-testid="shipment-details-form"
            key="shipment-details-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: IncidentDetails.formHeader,
      description: IncidentDetails.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={IncidentDetails.formName}
            formConfig={IncidentDetails.formConfig}
            formValidation={IncidentDetails.formValidation}
            data-testid="incident-details-form"
            key="incident-details-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
  ];

  const CARGO_STAGE2_STEPS: WizardProps.Step[] = [
    {
      title: ClaimDetails.formHeader,
      description: ClaimDetails.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={ClaimDetails.formName}
            formConfig={ClaimDetails.formConfig}
            formValidation={ClaimDetails.formValidation}
            data-testid="claim-details-form"
            key="claim-details-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
  ];

  const CARGO_STAGE3_STEPS: WizardProps.Step[] = [
    {
      title: FileAttachments.formHeader,
      description: FileAttachments.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={"Error"}>
          <FormContent
            formName={FileAttachments.formName}
            formConfig={FileAttachments.formConfig}
            formValidation={FileAttachments.formValidation}
            data-testid="file-attachments-form"
            key="file-attachments-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
  ];

  const CARGO_REVIEW_STEP: WizardProps.Step = {
    title: REVIEW_PAGE_CONSTANTS.FORM_NAME,
    description: REVIEW_PAGE_CONSTANTS.FORM_DESCRIPTION,
    content: <ReviewPage allFormConfigs={DEFAULT_FORM_CONFIGS} />,
    isOptional: isDevEnvironment(),
  };
  // Dynamic wizard configuration based on current stage
  // Each stage has different forms but shares the same review step pattern
  const CARGO_STEPS: Record<CARGO_STAGES, WizardProps.Step[]> = {
    [CARGO_STAGES.STAGE1]: [...CARGO_STAGE1_STEPS, CARGO_REVIEW_STEP],
    [CARGO_STAGES.STAGE2]: [...CARGO_STAGE2_STEPS, CARGO_REVIEW_STEP],
    [CARGO_STAGES.STAGE3]: [...CARGO_STAGE3_STEPS, CARGO_REVIEW_STEP],
  };

  const onStartNewClaim = () => {
    dispatch(resetCargoAppState());
    dispatch(resetFormsState());
    dispatch(resetWizardState());
    dispatch(resetReviewPageState());
    dispatch(resetAppState());
    dispatch(setLineOfBusiness(LineOfBusiness.CARGO));

    setFormattedFormErrors(null);
    setRenderSubmitScreen(false);
    setDuplicateModalDismissed(false);
    setClaimRetrievalErrorModalOpen(false);
    setClaimAlreadyCompletedModalOpen(false);

    wizardKey.current += 1;

    // Note: Stage 2/3 reset logic is now handled separately in handleModalConfirm
    // This function is only used for full resets (Stage 1 or other scenarios)
  };

  // Helper function to handle modal dismissal with form reset and redirect
  const handleModalDismissalWithRedirect = (
    modalType: "CLAIM_RETRIEVAL_ERROR" | "CLAIM_ALREADY_COMPLETED"
  ) => {
    onStartNewClaim();
    setResetFormModalOpen(false);

    // Close the specific modal based on type
    if (modalType === "CLAIM_RETRIEVAL_ERROR") {
      setClaimRetrievalErrorModalOpen(false);
    } else if (modalType === "CLAIM_ALREADY_COMPLETED") {
      setClaimAlreadyCompletedModalOpen(false);
    }

    // Redirect to cargo home page to start a new claim
    window.location.href = "/cargo";
  };

  // Handle modal actions based on modal type
  const handleModalConfirm = () => {
    if (resetFormModalContents.title === DUPLICATE_CHECK_ERROR_MODAL_TITLE) {
      // User chose to retry the duplicate check
      retryDuplicateCheck();
    } else if (
      resetFormModalContents.title === CLAIM_RETRIEVAL_ERROR_MODAL_TITLE
    ) {
      // User acknowledged the claim retrieval error - reset form, close modal and redirect to home
      handleModalDismissalWithRedirect("CLAIM_RETRIEVAL_ERROR");
      return;
    } else if (
      resetFormModalContents.title === CLAIM_ALREADY_COMPLETED_MODAL_TITLE
    ) {
      // User acknowledged the claim is already completed - reset form, close modal and redirect to home
      handleModalDismissalWithRedirect("CLAIM_ALREADY_COMPLETED");
      return;
    } else if (resetFormModalContents.title === "Reset Form") {
      // Handle reset form based on current stage
      const isStage2Or3 =
        currentStage === CARGO_STAGES.STAGE2 ||
        currentStage === CARGO_STAGES.STAGE3;

      if (isStage2Or3) {
        // For Stage 2/3: Only reset forms and wizard state, stay on current stage
        dispatch(resetFormsState());
        dispatch(resetWizardState());
        dispatch(resetReviewPageState());

        setFormattedFormErrors(null);
        setRenderSubmitScreen(false);
        setDuplicateModalDismissed(false);

        wizardKey.current += 1;

        // Stay on current stage - no redirect needed
        // The wizard will re-render with cleared form data but maintain the current stage
      } else {
        // For Stage 1: Full reset (existing behavior)
        onStartNewClaim();
      }
    } else {
      // Default behavior - reset form
      onStartNewClaim();
    }
    setResetFormModalOpen(false);
  };

  const handleModalCancel = () => {
    if (resetFormModalContents.title === DUPLICATE_CHECK_ERROR_MODAL_TITLE) {
      // User chose to continue anyway - clear error and close modal
      dispatch(
        setValueInCargoAppState({ key: "listCargoClaimsError", value: "" })
      );
    } else if (resetFormModalContents.title === DUPLICATE_MODAL_TITLE) {
      // User chose to continue with duplicate claim - mark as dismissed and navigate like no duplicates
      setDuplicateModalDismissed(true);
      handleNoDuplicatesFound();
    } else if (
      resetFormModalContents.title === CLAIM_RETRIEVAL_ERROR_MODAL_TITLE
    ) {
      // For claim retrieval error, cancel does the same as confirm - reset form, close and redirect
      handleModalDismissalWithRedirect("CLAIM_RETRIEVAL_ERROR");
      return;
    } else if (
      resetFormModalContents.title === CLAIM_ALREADY_COMPLETED_MODAL_TITLE
    ) {
      // For completed claim, cancel does the same as confirm - reset form, close and redirect
      handleModalDismissalWithRedirect("CLAIM_ALREADY_COMPLETED");
      return;
    }
    setResetFormModalOpen(false);
  };
  // Check for potential duplicate claims
  // Only runs once per form session to avoid unnecessary API calls
  const checkForDuplicates = () => {
    if (!duplicateClaimChecked) {
      const incidentDateIsValid = moment(
        incidentDate,
        "YYYY-MM-DD",
        true
      ).isValid();

      if (incidentDateIsValid && totalClaimValue && laneOrigination) {
        dispatch(
          listCargoClaims({
            input: {
              laneOrigination,
              incidentDate,
              totalClaimValue: formatCurrencyStringToNumber(totalClaimValue),
            },
          })
        );

        dispatch(
          setValueInCargoAppState({ key: "duplicateClaimChecked", value: true })
        );
      }
    }
  };

  // Retry duplicate check when API call fails
  const retryDuplicateCheck = () => {
    // Reset the duplicate check state to allow retry
    dispatch(
      setValueInCargoAppState({ key: "duplicateClaimChecked", value: false })
    );
    // Clear any existing error
    dispatch(
      setValueInCargoAppState({ key: "listCargoClaimsError", value: "" })
    );
    // Trigger the check again
    checkForDuplicates();
  };

  // Helper functions for duplicate check result handling
  const handleDuplicateCheckError = useCallback(() => {
    setResetFormModalContents({
      title: DUPLICATE_CHECK_ERROR_MODAL_TITLE,
      description: <DuplicateCheckErrorContent />,
      cancel: "Continue Anyway",
      confirm: "Retry Check",
    });
    setResetFormModalOpen(true);
  }, []);

  const handleDuplicatesFound = useCallback(() => {
    setResetFormModalContents({
      title: DUPLICATE_MODAL_TITLE,
      description: (
        <DuplicateClaimsFoundContent
          incidentDate={incidentDate}
          totalClaimValue={totalClaimValue}
          laneOrigination={laneOrigination}
        />
      ),
      cancel: "Yes",
      confirm: "No, Reset Form",
    });
    setResetFormModalOpen(true);
  }, [incidentDate, totalClaimValue, laneOrigination]);

  const handleNoDuplicatesFound = useCallback(() => {
    // No duplicates found - automatically advance to review step ONLY ONCE
    // Only navigate if we're currently on the incident details step and in stage 1
    if (
      !hasAutoNavigated &&
      currentStage === CARGO_STAGES.STAGE1 &&
      currentIndex ===
        CARGO_STEP_NAMES[CARGO_STAGES.STAGE1].indexOf(
          INCIDENT_DETAILS_CONSTANTS.FORM_NAME
        )
    ) {
      // Navigate to the review step (last step in current stage)
      const reviewStepIndex = CARGO_STEPS[currentStage].length - 1;
      dispatch(setSkipToStep({ value: reviewStepIndex }));
      setHasAutoNavigated(true);
    }
    // Ensure modal is closed when no duplicates are found
    setResetFormModalOpen(false);
  }, [hasAutoNavigated, currentStage, currentIndex, dispatch]);
  const onPrevious = () => {
    if (formattedFormErrors) setFormattedFormErrors(null);
  };
  const onNext = () => {
    // Trigger duplicate check after incident details are completed
    // This is the earliest point where we have all required data for duplicate detection
    if (
      currentStage === CARGO_STAGES.STAGE1 &&
      currentIndex ===
        CARGO_STEP_NAMES[CARGO_STAGES.STAGE1].indexOf(
          INCIDENT_DETAILS_CONSTANTS.FORM_NAME
        )
    ) {
      checkForDuplicates();
    }
  };
  const onSkipTo = (goingBack: boolean) => {
    if (goingBack) {
      if (formattedFormErrors) setFormattedFormErrors(null);
    }
  };
  const onBackToEdit = () => {
    setCurrentStep(CARGO_STEPS[currentStage].length - 1);
    setRenderSubmitScreen(false);
  };
  const onResetForm = () => {
    // Different behavior for Stage 2/3 vs Stage 1
    const isStage2Or3 =
      currentStage === CARGO_STAGES.STAGE2 ||
      currentStage === CARGO_STAGES.STAGE3;

    const description = (
      <TextContent>
        <Box textAlign="left">
          <div className="modal-text">
            <div>Are you sure you want to reset the form?</div>
            <div>
              {isStage2Or3
                ? "The current form data will be cleared, but you can continue with this claim stage."
                : "All progress will be lost and all of the form information will be deleted."}
            </div>
          </div>
        </Box>
      </TextContent>
    );
    setResetFormModalContents({
      title: "Reset Form",
      description,
      cancel: "Cancel",
      confirm: "Reset Form",
    });

    setResetFormModalOpen(true);
  };
  const onSubmit = () => {
    setSubmitClicked(true);
  };
  const submitStage1 = (): SimLinkClaimModel => {
    const businessDetails = formStates[BusinessDetails.formName];
    const shipmentDetails = formStates[ShipmentDetails.formName];
    const incidentDetails = formStates[IncidentDetails.formName];

    const formState: FormStateType = {
      ...businessDetails,
      ...shipmentDetails,
      ...incidentDetails,
    };
    const additionalFields = {
      user,
      amazonClaimReferenceId,
    };

    const symLinkClaimModel = convertFormToSymLinkClaimModel(formState, {
      amazonClaimReferenceId,
    });
    dispatch(createCargoClaim({ formState, additionalFields }));
    return symLinkClaimModel;
  };
  const submitStage2 = (): SimLinkClaimModel => {
    const claimDetails = formStates[ClaimDetails.formName];

    const formState: FormStateType = {
      ...claimDetails,
    };
    const additionalFields = {
      user,
      amazonClaimReferenceId,
      claimNumber,
      incidentDate: cargoIncidentDate,
    };
    const symLinkClaimModel = convertFormToSymLinkClaimModel(formState, {
      amazonClaimReferenceId,
    });

    dispatch(updateCargoClaim({ formState, additionalFields }));
    return symLinkClaimModel;
  };
  const submitStage3 = (): SimLinkClaimModel => {
    const fileAttachments = formStates[FileAttachments.formName];

    const formState: FormStateType = {
      ...fileAttachments,
    };
    const additionalFields = {
      user,
      amazonClaimReferenceId,
      claimNumber,
      incidentDate: cargoIncidentDate,
    };
    const symLinkClaimModel = convertFormToSymLinkClaimModel(formState, {
      amazonClaimReferenceId,
    });

    dispatch(
      updateCargoClaimAttachments({ formState, additionalFields })
    );
    return symLinkClaimModel;
  };
  const submitForm = () => {
    let symLinkClaimModel: SimLinkClaimModel | undefined = undefined;
    if (currentStage === CARGO_STAGES.STAGE1) {
      symLinkClaimModel = submitStage1();
    }
    if (currentStage === CARGO_STAGES.STAGE2) {
      symLinkClaimModel = submitStage2();
    }
    if (currentStage === CARGO_STAGES.STAGE3) {
      symLinkClaimModel = submitStage3();
    }
    setFormattedFormErrors(null);
    setSubmitClicked(false);
    setRenderSubmitScreen(true);
    setClaimModel(symLinkClaimModel);
  };
  // Initialize stage from URL parameter immediately to prevent flicker
  useEffect(() => {
    const claimNumber: string = params.get("claimNumber") ?? "";

    if (claimNumber && !initialStageSet) {
      // Determine stage based on claim number pattern or set to stage 2 as default for continuation
      // Since we're continuing an existing claim, it's likely stage 2 or 3
      // We'll set to stage 2 initially and let the API response correct it if needed
      dispatch(setCargoStage({ stage: CARGO_STAGES.STAGE2 }));
      setInitialStageSet(true);
    } else if (!claimNumber && !initialStageSet) {
      // Ensure we're on stage 1 for new claims
      dispatch(setCargoStage({ stage: CARGO_STAGES.STAGE1 }));
      setInitialStageSet(true);
    }
  }, [params, initialStageSet, dispatch]);

  // Handle deep linking to edit existing claims via URL parameters
  // location.key dependency ensures effect runs on navigation changes
  useEffect(() => {
    const claimNumber: string = params.get("claimNumber") ?? "";

    if (claimNumber) {
      onStartNewClaim();
      dispatch(setClaimNumber({ value: claimNumber }));
      dispatch(getCargoClaim({ claimNumber }));
    }
  }, [params, location.key]);

  // Error handling: Show modal if API call completes but no valid stage info is available
  // This handles cases where the API doesn't return submittedStage or returns invalid data
  useEffect(() => {
    const claimNumber: string = params.get("claimNumber") ?? "";

    // Only show error modal if:
    // 1. We have a claim number (existing claim)
    // 2. We're not currently loading
    // 3. We have submitted stage info but it's invalid (not a valid CARGO_STAGES value)
    // 4. Modal hasn't been shown yet
    // Note: We use a timeout to allow the stage determination logic to run first
    if (claimNumber && !loadingCargoClaim && !claimRetrievalErrorModalOpen) {
      const timeoutId = setTimeout(() => {
        // Check if we have valid stage info
        // cargoSubmittedStage should be one of: "STAGE1", "STAGE2", "STAGE3"
        const validStages = ["STAGE1", "STAGE2", "STAGE3"];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const hasValidStage =
          cargoSubmittedStage && validStages.includes(cargoSubmittedStage);

        // Show error only if we truly have no stage info (null, undefined, or empty string)
        // If we have valid stage info like "STAGE1", "STAGE2", "STAGE3", don't show error
        if (!cargoSubmittedStage && !claimRetrievalErrorModalOpen) {
          setResetFormModalContents({
            title: CLAIM_RETRIEVAL_ERROR_MODAL_TITLE,
            description: <ClaimRetrievalErrorContent />,
            cancel: "",
            confirm: "Close",
          });
          setClaimRetrievalErrorModalOpen(true);
          setResetFormModalOpen(true);
        }
      }, 1000); // 1 second delay to allow stage determination to complete

      return () => clearTimeout(timeoutId);
    }
  }, [
    claimNumber,
    loadingCargoClaim,
    cargoSubmittedStage,
    claimRetrievalErrorModalOpen,
  ]);

  // Timeout mechanism: Show error modal if API call takes too long
  useEffect(() => {
    const claimNumber: string = params.get("claimNumber") ?? "";

    if (claimNumber && loadingCargoClaim && !claimRetrievalErrorModalOpen) {
      // Set a timeout to show error modal if API takes longer than 30 seconds
      const timeoutId = setTimeout(() => {
        if (
          loadingCargoClaim &&
          !cargoSubmittedStage &&
          !claimRetrievalErrorModalOpen
        ) {
          setResetFormModalContents({
            title: CLAIM_RETRIEVAL_ERROR_MODAL_TITLE,
            description: <ClaimRetrievalErrorContent />,
            cancel: "",
            confirm: "Close",
          });
          setClaimRetrievalErrorModalOpen(true);
          setResetFormModalOpen(true);
        }
      }, 30000); // 30 seconds timeout

      return () => clearTimeout(timeoutId);
    }
  }, [
    claimNumber,
    loadingCargoClaim,
    cargoSubmittedStage,
    claimRetrievalErrorModalOpen,
  ]);

  // Handle stage determination from API response
  // This runs when we get claim data from the API (for existing claims)
  useEffect(() => {
    if (cargoSubmittedStage && claimNumber) {
      // When loading an existing claim, determine the next stage based on what was submitted
      const submittedStageParser: Record<CARGO_STAGES, CARGO_STAGES | null> = {
        [CARGO_STAGES.STAGE1]: CARGO_STAGES.STAGE2,
        [CARGO_STAGES.STAGE2]: CARGO_STAGES.STAGE3,
        [CARGO_STAGES.STAGE3]: null, // Claim is complete
      };

      const nextStage =
        submittedStageParser[cargoSubmittedStage as CARGO_STAGES];

      if (cargoSubmittedStage === CARGO_STAGES.STAGE3) {
        // Claim is already completed - show modal to inform user
        if (!claimAlreadyCompletedModalOpen) {
          setResetFormModalContents({
            title: CLAIM_ALREADY_COMPLETED_MODAL_TITLE,
            description: <ClaimAlreadyCompletedContent />,
            cancel: "",
            confirm: "Close",
          });
          setClaimAlreadyCompletedModalOpen(true);
          setResetFormModalOpen(true);
        }
      } else if (nextStage) {
        dispatch(setCargoStage({ stage: nextStage }));
      }
    } else if (cargoSubmittedStage && !claimNumber) {
      // This handles the case after successful submission of a new claim
      // Advance to next stage after successful submission
      const submittedStageParser: Record<CARGO_STAGES, CARGO_STAGES | null> = {
        [CARGO_STAGES.STAGE1]: CARGO_STAGES.STAGE2,
        [CARGO_STAGES.STAGE2]: CARGO_STAGES.STAGE3,
        [CARGO_STAGES.STAGE3]: null,
      };

      const nextStage =
        submittedStageParser[cargoSubmittedStage as CARGO_STAGES];
      if (nextStage) {
        dispatch(setCargoStage({ stage: nextStage }));
      }
    }
  }, [
    cargoSubmittedStage,
    claimNumber,
    dispatch,
    claimAlreadyCompletedModalOpen,
  ]);

  // useEffect to change steps based on currentStage
  useEffect(() => {
    setStepsToRender(CARGO_STEPS[currentStage]);
  }, [currentStage]);

  // Handle duplicate check results - show modal for duplicates, navigate when none found, or handle errors
  useEffect(() => {
    // Only process results when duplicate check has completed (not fetching) and was actually performed
    if (duplicateClaimChecked && !fetchingClaimsList) {
      if (listCargoClaimsError) {
        handleDuplicateCheckError();
      } else if (cargoClaimsList?.length && !duplicateModalDismissed) {
        handleDuplicatesFound();
      } else if (!cargoClaimsList?.length) {
        handleNoDuplicatesFound();
      }
    }
  }, [
    cargoClaimsList,
    fetchingClaimsList,
    duplicateClaimChecked,
    listCargoClaimsError,
    duplicateModalDismissed,
  ]);

  // Reset duplicate modal dismissed flag when duplicate check criteria change
  useEffect(() => {
    setDuplicateModalDismissed(false);
    // Clear previous duplicate results when criteria change
    dispatch(setValueInCargoAppState({ key: "cargoClaimsList", value: [] }));
    dispatch(
      setValueInCargoAppState({ key: "listCargoClaimsError", value: "" })
    );
  }, [incidentDate, totalClaimValue, laneOrigination, dispatch]);

  /**
   * Focus management for accessibility compliance
   * Screen readers need focus on error messages to announce validation failures
   */
  useEffect(() => {
    if (formattedFormErrors) {
      const errorElement = document.querySelector('[title="form-errors"]');
      if (errorElement) {
        (errorElement as HTMLElement).tabIndex = 0;
        (errorElement as HTMLElement).focus();
        window.scrollTo(0, document.body.scrollHeight);
      }
    }
  }, [formattedFormErrors]);
  /**
   * Critical form submission flow that handles validation timing
   * useLayoutEffect ensures this runs synchronously before browser paint
   * to prevent UI flicker during validation state changes
   */
  useLayoutEffect(() => {
    if (validating) {
      return;
    }
    if (!errors.length && submitClicked) {
      submitForm();
      return;
    }
    setSubmitClicked(false);
    if (errors.length) {
      const errorText: any = (
        <div role="alert" title="form-errors" tabIndex={-1}>
          <TextContent>
            <span className="form-page-error">{FORM_PAGE_ERROR_MESSAGE}</span>
          </TextContent>
        </div>
      );
      if (!goingBack) {
        dispatch(resetUpdatedForm());
        setFormattedFormErrors(errorText);
      }
    }
  }, [errors, validating, submitClicked]);

  // Show loading spinner while:
  // 1. Steps are not yet rendered (prevents flicker during stage determination)
  // 2. Loading claim data from API
  // 3. Initial stage hasn't been set yet (prevents showing wrong stage briefly)
  // Note: We don't check claimAlreadyCompletedModalOpen here because we need to render
  // the modal component below, not show a spinner
  if (!stepsToRender?.length || loadingCargoClaim || !initialStageSet) {
    return (
      <Box textAlign="center">
        <SpaceBetween direction="vertical" size="m">
          <Spinner data-testid="cargo-claim-spinner" size="large" />
          <Box textAlign="center" variant="p">
            {loadingCargoClaim ? "Loading Claim Details..." : "Initializing..."}
          </Box>
        </SpaceBetween>
      </Box>
    );
  }
  if (renderSubmitScreen) {
    return (
      <ClaimSubmitted
        key="claim submitted"
        onBackToEdit={onBackToEdit}
        onStartNewClaim={onStartNewClaim}
        claimModel={claimModel}
      />
    );
  }

  // If claim has been submitted and page was reloaded then we need to reset the state to start a new form
  if (claimSubmitSuccess && renderSubmitScreen === false) {
    onStartNewClaim();
  }
  return (
    <React.Fragment>
      <ResetFormModal
        setModalOpen={setResetFormModalOpen}
        onResetForm={handleModalConfirm}
        onCancel={handleModalCancel}
        isModalOpen={resetFormModalOpen}
        modalTitle={resetFormModalContents.title}
        modalDescription={resetFormModalContents.description}
        cancelButtonText={resetFormModalContents.cancel}
        confirmButtonText={resetFormModalContents.confirm}
      />
      <WizardWrapper
        onNext={onNext}
        onSubmit={onSubmit}
        onSkipTo={onSkipTo}
        onPrevious={onPrevious}
        onReset={onResetForm}
        currentStep={currentStep}
        stepNames={CARGO_STEP_NAMES[currentStage]}
        allFormValidations={FORM_VALIDATIONS[currentStage]}
        steps={stepsToRender}
        key={wizardKey.current}
        loadingNextStep={fetchingClaimsList}
      />
    </React.Fragment>
  );
}

export default CargoClaimIntakeApp;
